import type { ItemResponse } from '@finn-no/mobility-item-insertion-shared';

type RedirectConfig = {
  root: string;
  isBackNavigationEnabled: boolean;
  isWizardEnabled: boolean;
  model: string;
};

/**
 * Builds an item-scoped lookup URL for browser history rewriting.
 *
 * @param options - URL building options
 * @returns The complete item-scoped lookup URL
 */
function buildItemScopedLookupUrl(options: {
  root: string;
  itemId: string;
  model: string;
  orgId?: string;
  regnr?: string;
  variantId?: string;
}): string {
  const { root, itemId, model, orgId, regnr, variantId } = options;

  // Build path: /item/{itemId}/lookup[/{regnr}[/{variantId}]]
  let path = `${root}/item/${itemId}/lookup`;
  if (regnr) {
    path += `/${encodeURIComponent(regnr)}`;
    if (variantId) {
      path += `/${encodeURIComponent(variantId)}`;
    }
  }

  // Build query params
  const queryParams = new URLSearchParams();
  queryParams.set('model', model);
  if (orgId) {
    queryParams.set('orgId', orgId);
  }

  return `${path}?${queryParams.toString()}`;
}

type RedirectParams = {
  itemData: ItemResponse | null;
  effectiveItemId: string | undefined;
  orgId: string | undefined;
  config: RedirectConfig;
  /**
   * Registration number (for history URL building)
   */
  regnr?: string;
  /**
   * Variant ID (for history URL building)
   */
  variantId?: string;
};

/**
 * Handles redirection from vehicle lookup to item insertion page.
 *
 * This function:
 * 1. Rewrites browser history with item-scoped lookup URL (if item was just created and back navigation is enabled)
 * 2. Redirects to the item insertion page
 *
 * @param params - Configuration for the redirect
 */
export function redirectToItemInsertion({
  itemData,
  effectiveItemId,
  orgId,
  config,
  regnr,
  variantId,
}: RedirectParams): void {
  const isCreation = !effectiveItemId && itemData?.id;
  const targetItemId = effectiveItemId || itemData?.id;

  if (!targetItemId) {
    return; // Should not happen, but guard against it
  }

  // After item creation with back navigation enabled, rewrite URL so back button works
  if (isCreation && config.isBackNavigationEnabled) {
    const newPath = buildItemScopedLookupUrl({
      root: config.root,
      itemId: targetItemId.toString(),
      model: config.model,
      orgId,
      regnr,
      variantId,
    });
    window.history.pushState(null, '', newPath);
  }

  // Redirect to appropriate page
  // - If wizard is enabled: go to step 2 (user completed lookup)
  // - Otherwise: go to root page (user skipped lookup or wizard is disabled)
  const itemInsertionPath = config.isWizardEnabled
    ? `${config.root}/item/${targetItemId}/steps/2`
    : `${config.root}/item/${targetItemId}`;
  const url = `${itemInsertionPath}${orgId ? `?orgId=${orgId}` : ''}`;
  console.log('🚀 ~ redirectToItemInsertion ~ url:', url);
  window.location.assign(url);
}
